import { QueryKey } from "@tanstack/react-query";

import type {
	CreateReviewCycleRequestInput,
	CreateReviewCycleResponseJson,
	ExportReviewCycleReviewReportsDetailsBody,
	ExportReviewCycleReviewReportsDetailsResponseJson,
	GetReviewCycleDetailsResponseFrontend,
	GetReviewCycleDetailsResponseJson,
	GetReviewCycleEasyAddFormConfigsResponseJson,
	GetReviewCycleFormsSummaryResponse,
	GetReviewCyclePreviewSubjectsResultsRequestInput,
	GetReviewCyclePreviewSubjectsResultsResponseJson,
	GetReviewCycleSubjectsResponseFrontend,
	GetReviewCyclesResponseJson,
	PostReviewCycleCalculateBodyFrontend,
	PostReviewCycleCopySelectionResponseJson,
	PostReviewCyclePreviewSubjectsRequestInput,
	PostReviewCyclePreviewSubjectsResponseJson,
	ReviewCycleReportRowFrontend,
	ReviewCycleStatus,
	ReviewCycleType,
	ReviewDetailsFrontend,
	UpdateReviewCycleRequest,
	UpdateReviewCycleResponseJson,
} from "@performyard/service-contracts";
import { JSON_STREAM_DELIMITER } from "@performyard/utils-isomorphic";

import { queryClient } from "../../../queryClient";
import { internalRequest, internalStreamRequest, processJsonStream } from "./internal-base-request";

// ============================================================================
// GET /v1/reviews/review-cycles
// ============================================================================

export type GetReviewCyclesParams = {
	search?: string;
	status?: ReviewCycleStatus[];
	type?: ReviewCycleType;
	easyAddEnabled?: boolean;
	sortName?: string;
	sortDirection?: "asc" | "desc";
	skip?: number;
	limit?: number;
};

export const getReviewCyclesList = async (params: GetReviewCyclesParams) => {
	const searchParams = new URLSearchParams();

	if (params.search) searchParams.set("search", params.search);
	if (params.status?.length) {
		for (const s of params.status) {
			searchParams.append("status", s);
		}
	}
	if (params.type) searchParams.set("type", params.type);
	if (params.easyAddEnabled !== undefined) searchParams.set("easyAddEnabled", String(params.easyAddEnabled));
	if (params.sortName) searchParams.set("sortName", params.sortName);
	if (params.sortDirection) searchParams.set("sortDirection", params.sortDirection);
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));

	const queryString = searchParams.toString();
	const url = `/v1/reviews/review-cycles${queryString ? `?${queryString}` : ""}`;

	const [data] = await internalRequest<GetReviewCyclesResponseJson>(url);
	return data;
};

export const calculateReviewCycleStatus = async (reviewCycleId: string) => {
	const body: PostReviewCycleCalculateBodyFrontend = {
		reviewCycleId,
		actions: ["status"],
	};

	const [response] = await internalRequest("/v1/review-cycle/calculate", {
		method: "POST",
		body: JSON.stringify(body),
	});

	return response;
};

export const reviewCycleSubjects = async (reviewCycleId: string) => {
	const [response] = await internalRequest<GetReviewCycleSubjectsResponseFrontend>(
		`/v1/review-cycle/${reviewCycleId}/subjects`,
		{
			method: "GET",
		}
	);

	return response;
};

// Track active streams per review cycle to prevent concurrent streams
const activeStreams = new Map<string, AbortController>();

export const reviewCycleReviewsDetailsStream = async (
	reviewCycleId: string,
	queryKey: QueryKey,
	onChunk?: (chunk: { data: ReviewDetailsFrontend[] }) => void
) => {
	// Abort any existing stream for this review cycle before starting a new one
	const existingController = activeStreams.get(reviewCycleId);
	if (existingController) {
		existingController.abort();
		activeStreams.delete(reviewCycleId);
	}

	// Create a new abort controller for this stream
	const abortController = new AbortController();
	activeStreams.set(reviewCycleId, abortController);

	// Initialize cache to empty array
	queryClient.setQueryData<ReviewDetailsFrontend[]>(queryKey, []);

	try {
		const response = await internalStreamRequest(`/v1/review-cycle/${reviewCycleId}/reviews/details`, {
			method: "GET",
			signal: abortController.signal,
		});

		if (response.status !== 200) {
			throw new Error(`Failed to get review cycle reviews details: ${response.statusText}`);
		}

		await processJsonStream<ReviewDetailsFrontend>({
			response,
			delimiter: JSON_STREAM_DELIMITER,
			signal: abortController.signal,
			onItem: (parsed) => {
				parsed.reviewDueDate = parsed.reviewDueDate ? new Date(parsed.reviewDueDate) : null;
				parsed.completeDate = parsed.completeDate ? new Date(parsed.completeDate) : null;
				return parsed;
			},
			onParsed: (parsedData) => {
				onChunk?.({ data: parsedData });
				queryClient.setQueryData(queryKey, (current: ReviewDetailsFrontend[]) => {
					return current ? [...current, ...parsedData] : parsedData;
				});
			},
		});

		// Return the final accumulated result
		return queryClient.getQueryData<ReviewDetailsFrontend[]>(queryKey) ?? [];
	} catch (error: any) {
		// Don't throw on abort - this is expected behavior
		if (error.name === "AbortError") {
			return [];
		}
		throw error;
	} finally {
		// Clean up the active stream tracking
		activeStreams.delete(reviewCycleId);
	}
};

// Track active report streams per review cycle to prevent concurrent streams
const activeReportStreams = new Map<string, AbortController>();

export const reviewCycleReportsStream = async (
	reviewCycleId: string,
	queryKey: QueryKey,
	onChunk?: (chunk: { data: ReviewCycleReportRowFrontend[] }) => void
) => {
	// Abort any existing stream for this review cycle before starting a new one
	const existingController = activeReportStreams.get(reviewCycleId);
	if (existingController) {
		existingController.abort();
		activeReportStreams.delete(reviewCycleId);
	}

	const abortController = new AbortController();
	activeReportStreams.set(reviewCycleId, abortController);

	// Initialize cache to empty array
	queryClient.setQueryData<ReviewCycleReportRowFrontend[]>(queryKey, []);

	try {
		const response = await internalStreamRequest(`/v1/reviews/review-cycles/${reviewCycleId}/reports`, {
			method: "GET",
			signal: abortController.signal,
		});

		if (response.status !== 200) {
			throw new Error(`Failed to get review cycle reports: ${response.statusText}`);
		}

		await processJsonStream<ReviewCycleReportRowFrontend>({
			response,
			delimiter: JSON_STREAM_DELIMITER,
			signal: abortController.signal,
			onItem: (parsed) => {
				parsed.authorDueDate = parsed.authorDueDate ? new Date(parsed.authorDueDate) : null;
				parsed.signerDueDate = parsed.signerDueDate ? new Date(parsed.signerDueDate) : null;
				return parsed;
			},
			onParsed: (parsedData) => {
				onChunk?.({ data: parsedData });
				queryClient.setQueryData(queryKey, (current: ReviewCycleReportRowFrontend[]) => {
					return current ? [...current, ...parsedData] : parsedData;
				});
			},
		});

		return queryClient.getQueryData<ReviewCycleReportRowFrontend[]>(queryKey) ?? [];
	} catch (error: any) {
		// Don't throw on abort - this is expected behavior
		if (error.name === "AbortError") {
			return [];
		}
		throw error;
	} finally {
		activeReportStreams.delete(reviewCycleId);
	}
};

export const deleteReviewFromReviewCycle = async (reviewCycleId: string, reviewId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/reviews/${reviewId}`, {
		method: "DELETE",
	});

	return response;
};

export const getReviewCycleDetails = async (reviewCycleID: string) => {
	const [data] = await internalRequest<GetReviewCycleDetailsResponseJson>(
		`/v1/review-cycle/${reviewCycleID}/details`
	);

	const { startDate, dueDate, autoflow, lookupWorkflow, ...reviewCycleData } = data;

	let parsedAutoflow: GetReviewCycleDetailsResponseFrontend["autoflow"] | null = null;
	if (autoflow?.trigger?.type === "scheduled") {
		// Convert strings to dates
		parsedAutoflow = {
			...autoflow,
			createdAt: autoflow.createdAt ? new Date(autoflow.createdAt) : undefined,
			deletedAt: autoflow.deletedAt ? new Date(autoflow.deletedAt) : undefined,
			updatedAt: autoflow.updatedAt ? new Date(autoflow.updatedAt) : undefined,
			trigger: {
				...autoflow.trigger,
				scheduled: {
					...autoflow.trigger.scheduled,
					inauguralTimeDate: autoflow.trigger.scheduled.inauguralTimeDate
						? new Date(autoflow.trigger.scheduled.inauguralTimeDate)
						: undefined,
					startTimeDate: autoflow.trigger.scheduled.startTimeDate
						? new Date(autoflow.trigger.scheduled.startTimeDate)
						: undefined,
					endTimeDate: autoflow.trigger.scheduled.endTimeDate
						? new Date(autoflow.trigger.scheduled.endTimeDate)
						: undefined,
				},
			},
		};
	}

	let parsedLookupWorkflow: GetReviewCycleDetailsResponseFrontend["lookupWorkflow"] | null = null;
	if (lookupWorkflow) {
		parsedLookupWorkflow = {
			...lookupWorkflow,
			deletedAt: lookupWorkflow.deletedAt ? new Date(lookupWorkflow.deletedAt) : undefined,
		};
	}

	const parsedResp: GetReviewCycleDetailsResponseFrontend = {
		...reviewCycleData,
		startDate: new Date(startDate),
		dueDate: dueDate ? new Date(dueDate) : null,
		autoflow: parsedAutoflow,
		lookupWorkflow: parsedLookupWorkflow,
	};

	return parsedResp;
};

export const getReviewCycleEasyAddFormConfigs = async (
	reviewCycleId: string
): Promise<GetReviewCycleEasyAddFormConfigsResponseJson["data"]> => {
	const [response] = await internalRequest<GetReviewCycleEasyAddFormConfigsResponseJson>(
		`/v1/reviews/review-cycles/${reviewCycleId}/easy-add-form-configs`,
		{ method: "GET" }
	);

	return response.data;
};

export const getReviewCycleFormsSummary = async (
	reviewCycleId: string
): Promise<GetReviewCycleFormsSummaryResponse["data"]> => {
	const [response] = await internalRequest<GetReviewCycleFormsSummaryResponse>(
		`/v1/reviews/review-cycles/${reviewCycleId}/forms-summary`,
		{ method: "GET" }
	);

	return response.data;
};

export const closeReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/close`, {
		method: "POST",
	});

	return response;
};

export const bulkDeactivateEasyAddFormsForReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/easy-add-bulk-deactivate`, {
		method: "POST",
	});

	return response;
};

export const bulkApproveEasyAddFormsForReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/easy-add-bulk-approve`, {
		method: "POST",
	});

	return response;
};

export const deleteReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}`, {
		method: "DELETE",
	});

	return response;
};

export const downloadGoalDiscussionCsv = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/goal-discussion-csv-download`, {
		method: "GET",
	});

	return response;
};

export const getGoalDiscussionCsvDownloadStatus = async (jobId: string) => {
	const [response] = await internalRequest(`/v1/async-job/${jobId}/status`, {
		method: "GET",
	});

	return response;
};

export const createReviewCycle = async (
	body: CreateReviewCycleRequestInput
): Promise<CreateReviewCycleResponseJson> => {
	const [response] = await internalRequest<CreateReviewCycleResponseJson>(`/v1/reviews/review-cycles`, {
		method: "POST",
		body: JSON.stringify(body),
	});
	return response;
};

export const updateReviewCycle = async (
	reviewCycleId: string,
	body: UpdateReviewCycleRequest
): Promise<UpdateReviewCycleResponseJson> => {
	const [response] = await internalRequest<UpdateReviewCycleResponseJson>(
		`/v1/reviews/review-cycles/${reviewCycleId}`,
		{
			method: "PUT",
			body: JSON.stringify(body),
		}
	);
	return response;
};

export const createReviewCycleCopySelection = async (
	reviewCycleId: string
): Promise<PostReviewCycleCopySelectionResponseJson> => {
	const [data] = await internalRequest<PostReviewCycleCopySelectionResponseJson>(
		`/v1/reviews/review-cycles/${reviewCycleId}/copy-selection`,
		{ method: "POST" }
	);
	return data;
};

export const postReviewCyclePreviewSubjects = async (
	body: PostReviewCyclePreviewSubjectsRequestInput
): Promise<PostReviewCyclePreviewSubjectsResponseJson> => {
	const [response] = await internalRequest<PostReviewCyclePreviewSubjectsResponseJson>(
		`/v1/reviews/review-cycles/preview-subjects`,
		{
			method: "POST",
			body: JSON.stringify(body),
		}
	);
	return response;
};

export const getReviewCyclePreviewSubjectsResults = async (
	previewId: string,
	params: GetReviewCyclePreviewSubjectsResultsRequestInput
): Promise<GetReviewCyclePreviewSubjectsResultsResponseJson> => {
	const search = new URLSearchParams();
	if (params.search) search.set("search", params.search);
	if (params.sortName) search.set("sortName", params.sortName);
	if (params.sortDirection) search.set("sortDirection", params.sortDirection);
	if (params.skip !== undefined) search.set("skip", String(params.skip));
	if (params.limit !== undefined) search.set("limit", String(params.limit));

	const [response] = await internalRequest<GetReviewCyclePreviewSubjectsResultsResponseJson>(
		`/v1/reviews/review-cycles/preview-subjects/${previewId}/results?${search.toString()}`,
		{ method: "GET" }
	);
	return response;
};

export const exportReviewCycleReviewReportsDetailsCsv = async (
	reviewCycleId: string,
	body: ExportReviewCycleReviewReportsDetailsBody
) => {
	const [response] = await internalRequest<ExportReviewCycleReviewReportsDetailsResponseJson>(
		`/v1/review-cycle/${reviewCycleId}/review-reports/details/export-csv`,
		{
			method: "POST",
			body: JSON.stringify(body),
		}
	);

	return response;
};
